import { z } from "zod";
import { request } from "./client";

/**
 * POST /signup — the only call that creates an account.
 *
 * ## It is anonymous, and that is not an oversight
 *
 * `anonymous: true` skips the Authorization header. The caller has no account
 * yet, so there is no token to send; without the flag the client would attach
 * whatever stale token happened to be in sessionStorage, which is worse than
 * sending none — a signup would inherit someone else's identity on a shared
 * machine.
 *
 * ## There is no password field, and there is nowhere to put one
 *
 * The server calls Cognito's AdminCreateUser, which generates a temporary
 * password and mails it. This keeps port.ts's guarantee intact even though the
 * form now lives on our own site: "Password handling never touches our code."
 * If a `password` ever appears in this interface, that guarantee is gone —
 * there is a test asserting the form renders no password input.
 *
 * ## The response says nothing about whether an account was created
 *
 * A duplicate email returns the same body as a new one. Telling them apart is
 * an account-enumeration oracle, and on a gambling-adjacent product the mere
 * existence of an account is sensitive. So the only honest thing the UI can say
 * is "check your email" — which is also true in both cases.
 */

const signupAckSchema = z.object({ status: z.string() });

export interface SignupRequest {
  email: string;
  givenName: string;
  familyName: string;
  /** YYYY-MM-DD, exactly as `<input type="date">` produces it. */
  birthdate: string;
  /** ISO-3166 alpha-2. Only AU qualifies; the server refuses anything else. */
  country: string;
  termsVersion: string;
}

export async function signUp(r: SignupRequest): Promise<void> {
  await request("/signup", signupAckSchema, {
    method: "POST",
    anonymous: true,
    body: {
      ...r,
      acceptedTerms: true,
      /* Bundled with the terms by ConsentNotice, and sent as a value rather
         than assumed server-side so a preference centre can one day write false
         without the record shape changing. */
      marketingOptIn: true,
    },
  });
}
